import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Crosshair, ArrowLeft, ExternalLink, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pill = ({ v }) => {
  if (!v) return null;
  const key = String(v).toLowerCase().replace(/\s+/g, "_");
  return <span className={`pill p-${key}`}>{String(v).replace(/_/g, " ")}</span>;
};

const auditCounts = (r) => {
  const a = Array.isArray(r?.citation_audit) ? r.citation_audit : [];
  const c = { verified: 0, confirm: 0, unverified: 0 };
  a.forEach((x) => { if (c[x.status] !== undefined) c[x.status] += 1; });
  return c;
};

const Column = ({ row, diverges }) => {
  if (!row) return <div className="cmp-col" data-testid="compare-missing"><div className="cmp-head"><div className="cmp-title">Not found</div></div><p className="cmp-block">This analysis could not be loaded.</p></div>;
  const r = row.result || {};
  const opts = Array.isArray(r.options) ? r.options.slice(0, 3) : [];
  const ac = auditCounts(r);
  return (
    <div className="cmp-col" data-testid={`compare-col-${row.id}`}>
      <div className="cmp-head">
        <div className="cmp-title">{row.title || "Untitled decision"}</div>
        <div className="cmp-sub">{new Date(row.created_at).toLocaleString()}</div>
      </div>
      <div className={`cmp-verdict ${diverges ? "diverges" : ""}`}>
        <span className="cmp-lab">Verdict</span>
        <span style={{ textTransform: "capitalize", color: "#fbf7ec", fontWeight: 600 }}>{(r.overall?.assessment || "mixed").replace(/_/g, " ")}</span>
        <Pill v={r.overall?.confidence} />
      </div>
      <div className="cmp-block"><span className="cmp-lab">Summary</span><p>{r.summary || "—"}</p></div>
      <div className="cmp-block">
        <span className="cmp-lab">Top options</span>
        {opts.length ? opts.map((o, i) => (<div className="cmp-opt" key={i}><b>{o.route}</b> <Pill v={o.strength} /></div>)) : <p>—</p>}
      </div>
      <div className="cmp-block">
        <span className="cmp-lab">Citation audit</span>
        <div className="cmp-audit">
          <span className="pill p-verified">verified {ac.verified}</span>
          <span className="pill p-confirm">confirm {ac.confirm}</span>
          <span className="pill p-unverified">unverified {ac.unverified}</span>
        </div>
      </div>
      <Link to={`/report/${row.id}`} className="fl-btn fl-btn--ghost fl-btn--sm" data-testid={`compare-open-${row.id}`}>Open full report <ExternalLink size={14} /></Link>
    </div>
  );
};

export default function Compare() {
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3);
  const [rows, setRows] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const results = await Promise.all(ids.map((id) => axios.get(`${API}/analyses/${id}`).then((r) => r.data).catch(() => null)));
        if (!live) return;
        setRows(results); setState("ok");
      } catch (e) { if (live) setState("error"); }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const verdicts = (rows || []).filter(Boolean).map((r) => r.result?.overall?.assessment || "mixed");
  const diverges = verdicts.length >= 2 && new Set(verdicts).size > 1;

  return (
    <div className="fl-app">
      <div className="fl-top">
        <div className="fl-wrap">
          <div className="fl-brandrow">
            <Link to="/" className="fl-brand" style={{ textDecoration: "none" }}>
              <Crosshair size={28} color="#c9a24b" />
              <div>
                <div className="fl-mark">LEX<span>GAP</span></div>
                <div className="fl-domain">lexgap.app</div>
              </div>
            </Link>
            <Link to="/" className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="compare-back-btn"><ArrowLeft size={15} /> Back to audit tool</Link>
          </div>
          <p className="fl-tag"><b>Side-by-side comparison</b> — up to three determinations, compared on verdict, options and citation grounding.</p>
        </div>
      </div>
      <div className="fl-wrap">
        {state === "loading" && (
          <div className="fl-card fl-loading" data-testid="compare-loading"><div className="fl-spin" /><div style={{ color: "#fbf7ec", fontWeight: 600 }}>Loading comparison…</div></div>
        )}
        {state === "ok" && (
          ids.length >= 2 ? (
            <>
              {diverges && <div className="cmp-diverge" data-testid="compare-diverge-banner"><AlertTriangle size={16} /> Verdicts diverge across these audits — see the highlighted rows.</div>}
              <div className="cmp-grid" style={{ gridTemplateColumns: `repeat(${ids.length}, 1fr)` }} data-testid="compare-grid">
                {ids.map((id, i) => <Column key={id} row={(rows || [])[i]} diverges={diverges} />)}
              </div>
            </>
          ) : (
            <div className="fl-empty" data-testid="compare-invalid">
              <div className="ic"><AlertTriangle size={30} /></div>
              <h3>Pick two or three audits to compare</h3>
              <p>Select two or three saved audits from the tool's recent list, then choose “Compare selected”.</p>
              <Link to="/" className="fl-btn fl-btn--primary" style={{ marginTop: 16 }}>Back to LexGap</Link>
            </div>
          )
        )}
      </div>
    </div>
  );
}
